import React, { Fragment } from 'react';
import { gql, useQuery } from '@apollo/client';
import propTypes from 'prop-types';
import { NextSeo, ProductJsonLd } from 'next-seo';

import { Product } from '../../components/product';

import { useAuth } from '../../providers/auth';

import { initializeApollo } from '../../lib/auth/apollo';
import { useRouter } from 'next/router';
import { getNav } from '../../lib/get-nav';
import { PageLayout } from '../../components/page-layout';
import { Center, Spinner } from '@chakra-ui/react';
import Page404 from '../404';
import { PageSpinner } from '../../components/page-spinner';

const GET_PRODUCT = gql`
  query Product($slug: String!) {
    product(slug: $slug) {
      id
      slug
      brand {
        id
        slug
        name
        returnPolicy
        returnPolicyCustomNDays
        returnAndExchangeText
        shippingDescription
        shippingSpeedLowerBound
        shippingSpeedUpperBound
        shipsFrom {
          country {
            country
          }
        }
      }
      rating
      name
      description
      techSpecs
      inStock
      alerts
      tags {
        tagName
      }
      reviews(first: 5) {
        pageInfo {
          hasNextPage
          endCursor
        }
        totalCount
        edges {
          node {
            id
            headline
            reviewText
            starRating
            user {
              publicName
            }
          }
        }
      }
      pricing {
        retailPrice {
          currency
          net {
            amount
          }
        }
        userDiscountedPrice {
          currency
          net {
            amount
          }
        }
        discount
      }
      thumbnail {
        url
      }
      images {
        id
        alt
        url
      }
      variants {
        id
        name
        quantity
        images {
          id
          alt
          url
        }
        pricing {
          retailPrice {
            currency
            net {
              amount
            }
          }
          userDiscountedPrice {
            currency
            net {
              amount
            }
          }
          discount
        }
        attributes {
          attribute {
            id
            name
          }
          values {
            id
            name
          }
        }
      }
    }
  }
`;

export async function getStaticProps({ params: { slug } }) {
  const apolloClient = initializeApollo();

  await Promise.all([
    getNav(apolloClient),
    // apolloClient.query({
    //   query: GET_PRODUCT,
    //   variables: { slug },
    // }),
  ]);

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      slug,
    },
    revalidate: 100,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  };
}

const ProductPage = () => {
  const router = useRouter();
  const { isAuthed } = useAuth();

  const { data, loading } = useQuery(GET_PRODUCT, {
    variables: { slug: router.query.slug },
    skip: !router.query.slug || !isAuthed,
  });
  if (router.isFallback) {
    return <PageSpinner />;
  }

  if (loading) {
    return (
      <PageLayout>
        <Center flex={1}>
          <Spinner size="xl" />
        </Center>
      </PageLayout>
    );
  }

  if (isAuthed && !loading && data?.product === null) {
    return <Page404 />;
  }
  return (
    <Fragment>
      {data?.product && (
        <>
          <NextSeo
            title={`${data.product.name} | Outdoorly`}
            description={data.product.description}
            openGraph={{
              url: `https://www.outdoorly.com/products/${router.query.slug}`,
              title: data.product.name,
              description: data.product.description,
              images: data.product.images.map((image) => ({
                url: image.url,
              })),
            }}
          />
          <ProductJsonLd
            productName={data.product.name}
            images={data.product.images.map((image) => image.url)}
            description={data.product.description}
            brand={data.product.brand.name}
            reviews={data.product.reviews?.edges?.map((node) => ({
              author: {
                type: 'Person',
                name: node?.review?.user?.publicName,
              },
              reviewBody: node?.review?.reviewText,
              name: node?.review?.headline,
              reviewRating: {
                ratingValue: node?.review?.starRating,
              },
            }))}
            aggregateRating={{
              ratingValue: data.product.rating,
              reviewCount: data.product.reviews?.totalCount,
            }}
            aggregateOffer={{
              lowPrice: data.product.pricing?.retailPrice?.net?.amount,
              priceCurrency: data.product.pricing?.retailPrice?.currency,
            }}
          />
        </>
      )}
      <PageLayout>
        <Product product={data?.product} />
      </PageLayout>
    </Fragment>
  );
};

ProductPage.propTypes = {
  slug: propTypes.string.isRequired,
};

export default ProductPage;
